import React, { useEffect, useState, useCallback } from "react";
import {
    Megaphone, Plus, Save, Trash2, Copy, Eye, EyeOff, X, AlertTriangle,
    LayoutTemplate, BarChart3, Clock,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { AnnouncementBanner } from "./AnnouncementBanner";
import { ModalPortal } from "./ModalPortal";

const STYLE_OPTIONS = [
    { key: "card", label: "Card overlay" },
    { key: "bar", label: "Sticky bar" },
    { key: "modal", label: "Popup modal" },
    { key: "hero", label: "Hero banner" },
];

const ICON_OPTIONS = ["plane", "alert", "truck", "sale", "gift", "sparkles", "megaphone", "calendar", "package", "clock", "zap", "shield"];

const BLANK = {
    active: false, priority: 10, style: "card",
    headline: "", subhead: "", description: "",
    icon: "megaphone", badge_text: "",
    cta_text: "", cta_url: "",
    bg_color: "#0a0905", accent_color: "#E9BC0B",
    dismissible: true, remember_days: 7,
    start_at: "", end_at: "", countdown_at: "",
    info_tiles: [], template: "custom",
};

export const AdminAnnouncementsPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [items, setItems] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [editing, setEditing] = useState(null); // announcement being edited or created
    const [previewOn, setPreviewOn] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        try {
            const [list, tpl] = await Promise.all([
                api.get("/admin/announcements", { headers }),
                api.get("/admin/announcements/templates", { headers }),
            ]);
            setItems(list.data || []);
            setTemplates(tpl.data || []);
        } catch {
            toast.error("Failed to load announcements");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const createNew = () => setEditing({ ...BLANK });
    const createFromTemplate = (t) => setEditing({ ...BLANK, ...t, template: t.key });

    const save = async () => {
        if (!editing.headline) {
            toast.error("Headline is required");
            return;
        }
        try {
            const payload = { ...editing };
            // convert empty strings to null so backend validation doesn't reject datetime-format checks
            ["start_at", "end_at", "countdown_at"].forEach((k) => { if (!payload[k]) payload[k] = null; });
            if (payload.id) {
                await api.patch(`/admin/announcements/${payload.id}`, payload, { headers });
                toast.success("Announcement updated");
            } else {
                await api.post("/admin/announcements", payload, { headers });
                toast.success("Announcement created");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const toggleActive = async (a) => {
        try {
            await api.patch(`/admin/announcements/${a.id}`, { active: !a.active }, { headers });
            load();
        } catch { toast.error("Toggle failed"); }
    };

    const duplicate = (a) => setEditing({ ...a, id: undefined, active: false, headline: `${a.headline} (Copy)`, views: 0, clicks: 0, dismissals: 0 });

    const remove = async (id) => {
        try {
            await api.delete(`/admin/announcements/${id}`, { headers });
            toast.success("Deleted");
            setConfirmDelete(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="space-y-5" data-testid="admin-announcements-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
                        <Megaphone size={18} className="text-[var(--gold)]" /> Homepage announcements
                    </h2>
                    <p className="text-xs text-[var(--text-muted)] font-mono mt-1">
                        Highest-priority active + scheduled announcement is shown at the top of the homepage.
                    </p>
                </div>
                <button onClick={createNew} className="btn-neon" data-testid="ann-new-button">
                    <Plus size={14} /> New announcement
                </button>
            </div>

            {/* Templates */}
            {templates.length > 0 && (
                <div className="glass-elevated rounded-2xl p-5" data-testid="ann-templates">
                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)] mb-3 flex items-center gap-1.5">
                        <LayoutTemplate size={11} /> Start from a template
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
                        {templates.map((t) => (
                            <button
                                key={t.key}
                                onClick={() => createFromTemplate(t)}
                                className="text-left p-3 rounded-xl border border-white/10 hover:border-[var(--gold)]/40 bg-black/20 transition-all"
                                data-testid={`ann-template-${t.key}`}
                            >
                                <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)]">{t.label}</p>
                                <p className="text-xs text-white font-semibold mt-1 truncate">{t.headline}</p>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* List */}
            <div className="space-y-3">
                {items.length === 0 && !editing && (
                    <div className="glass rounded-2xl p-8 text-center text-[var(--text-secondary)]" data-testid="ann-empty">
                        No announcements yet. Create your first one above.
                    </div>
                )}
                {items.map((a) => (
                    <div key={a.id} className="glass-elevated rounded-2xl p-4" data-testid={`ann-row-${a.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 text-white/60">
                                        {a.style}
                                    </span>
                                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${a.active ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-white/10 text-white/40"}`}>
                                        {a.active ? "Active" : "Inactive"}
                                    </span>
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                        Priority {a.priority}
                                    </span>
                                </div>
                                <p className="font-display text-lg font-bold text-white mt-1 truncate">{a.headline}</p>
                                {a.subhead && <p className="text-xs text-[var(--gold)] font-semibold truncate">{a.subhead}</p>}
                                <div className="flex items-center gap-3 mt-2 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                    <span className="inline-flex items-center gap-1"><BarChart3 size={10} /> {a.views || 0} views</span>
                                    <span>· {a.clicks || 0} clicks</span>
                                    <span>· {a.dismissals || 0} dismissals</span>
                                    {a.end_at && <span className="inline-flex items-center gap-1"><Clock size={10} /> ends {new Date(a.end_at).toLocaleDateString("en-AU")}</span>}
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                <button onClick={() => toggleActive(a)} className={`px-3 py-1.5 rounded-full border text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 ${a.active ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "border-white/10 text-white/60 hover:bg-white/5"}`} data-testid={`ann-toggle-${a.id}`}>
                                    {a.active ? <><Eye size={11} /> On</> : <><EyeOff size={11} /> Off</>}
                                </button>
                                <button onClick={() => setEditing({ ...a })} className="px-3 py-1.5 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 text-[10px] font-mono uppercase tracking-widest" data-testid={`ann-edit-${a.id}`}>
                                    Edit
                                </button>
                                <button onClick={() => duplicate(a)} className="px-3 py-1.5 rounded-full border border-white/10 text-[var(--text-secondary)] hover:border-white/25 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1" data-testid={`ann-duplicate-${a.id}`}>
                                    <Copy size={11} /> Copy
                                </button>
                                <button onClick={() => setConfirmDelete(a)} className="px-3 py-1.5 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1" data-testid={`ann-delete-${a.id}`}>
                                    <Trash2 size={11} /> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Editor drawer */}
            {editing && (
                <ModalPortal>
                <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)} data-testid="ann-editor-modal">
                    <div className="glass-elevated rounded-2xl p-6 max-w-3xl w-full my-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-lg font-bold text-white">{editing.id ? "Edit announcement" : "New announcement"}</h3>
                            <button onClick={() => setEditing(null)} className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/5" aria-label="Close">
                                <X size={14} className="text-white/60" />
                            </button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-3">
                            <Labeled label="Headline (required)"><input value={editing.headline} onChange={(e) => setEditing({ ...editing, headline: e.target.value })} className={inp} data-testid="ann-input-headline" /></Labeled>
                            <Labeled label="Subhead / dates line"><input value={editing.subhead} onChange={(e) => setEditing({ ...editing, subhead: e.target.value })} className={inp} data-testid="ann-input-subhead" /></Labeled>
                            <Labeled label="Description" className="md:col-span-2"><textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={`${inp} resize-y`} data-testid="ann-input-description" /></Labeled>
                            <Labeled label="Badge text (top pill)"><input value={editing.badge_text} onChange={(e) => setEditing({ ...editing, badge_text: e.target.value })} className={inp} data-testid="ann-input-badge" /></Labeled>
                            <Labeled label="Icon">
                                <select value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} className={inp} data-testid="ann-input-icon">
                                    {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                                </select>
                            </Labeled>
                            <Labeled label="Style">
                                <select value={editing.style} onChange={(e) => setEditing({ ...editing, style: e.target.value })} className={inp} data-testid="ann-input-style">
                                    {STYLE_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                                </select>
                            </Labeled>
                            <Labeled label="Priority (higher wins)"><input type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: Number(e.target.value) })} className={inp} data-testid="ann-input-priority" /></Labeled>
                            <Labeled label="CTA text"><input value={editing.cta_text} onChange={(e) => setEditing({ ...editing, cta_text: e.target.value })} className={inp} data-testid="ann-input-cta-text" /></Labeled>
                            <Labeled label="CTA URL"><input value={editing.cta_url} onChange={(e) => setEditing({ ...editing, cta_url: e.target.value })} placeholder="/contact or https://…" className={inp} data-testid="ann-input-cta-url" /></Labeled>
                            <Labeled label="Background color"><input type="color" value={editing.bg_color || "#0a0905"} onChange={(e) => setEditing({ ...editing, bg_color: e.target.value })} className={inp} data-testid="ann-input-bg" /></Labeled>
                            <Labeled label="Accent color"><input type="color" value={editing.accent_color || "#E9BC0B"} onChange={(e) => setEditing({ ...editing, accent_color: e.target.value })} className={inp} data-testid="ann-input-accent" /></Labeled>
                            <Labeled label="Start (optional)"><input type="datetime-local" value={editing.start_at || ""} onChange={(e) => setEditing({ ...editing, start_at: e.target.value })} className={inp} data-testid="ann-input-start" /></Labeled>
                            <Labeled label="End (optional)"><input type="datetime-local" value={editing.end_at || ""} onChange={(e) => setEditing({ ...editing, end_at: e.target.value })} className={inp} data-testid="ann-input-end" /></Labeled>
                            <Labeled label="Countdown target (optional)"><input type="datetime-local" value={editing.countdown_at || ""} onChange={(e) => setEditing({ ...editing, countdown_at: e.target.value })} className={inp} data-testid="ann-input-countdown" /></Labeled>
                            <Labeled label="Remember dismiss (days)"><input type="number" min="0" max="365" value={editing.remember_days ?? 0} onChange={(e) => setEditing({ ...editing, remember_days: Number(e.target.value) })} className={inp} data-testid="ann-input-remember" /></Labeled>
                        </div>

                        <div className="flex items-center gap-5 mt-4">
                            <Toggle label="Active" value={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} testid="ann-input-active" />
                            <Toggle label="Dismissible" value={editing.dismissible} onChange={(v) => setEditing({ ...editing, dismissible: v })} testid="ann-input-dismissible" />
                        </div>

                        <InfoTilesEditor tiles={editing.info_tiles || []} onChange={(tiles) => setEditing({ ...editing, info_tiles: tiles })} />

                        <div className="flex flex-wrap gap-2 justify-end mt-5">
                            <button onClick={() => setPreviewOn((v) => !v)} className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:border-white/25" data-testid="ann-preview">
                                {previewOn ? "Hide preview" : "Preview"}
                            </button>
                            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:border-white/25" data-testid="ann-cancel">Cancel</button>
                            <button onClick={save} className="btn-solid" data-testid="ann-save"><Save size={14} /> Save</button>
                        </div>

                        {previewOn && (
                            <div className="mt-6 pt-6 border-t border-white/10">
                                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)] mb-3">Preview</p>
                                <div data-testid="ann-preview-render">
                                    <AnnouncementBanner previewData={{ ...editing, id: "preview" }} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                </ModalPortal>
            )}

            {/* Delete confirm */}
            {confirmDelete && (
                <ModalPortal>
                <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
                    <div className="glass-elevated rounded-2xl p-6 max-w-md w-full border border-red-500/30" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-3">
                            <AlertTriangle size={18} className="text-red-300" />
                            <h3 className="font-display text-lg font-bold text-white">Delete announcement?</h3>
                        </div>
                        <p className="text-sm text-[var(--text-secondary)] mb-5">
                            &quot;{confirmDelete.headline}&quot; will be permanently removed.
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:border-white/25">Cancel</button>
                            <button onClick={() => remove(confirmDelete.id)} className="px-4 py-2 rounded-full bg-red-500/20 border border-red-500/50 text-red-200 hover:bg-red-500/30 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1" data-testid="ann-confirm-delete"><Trash2 size={12} /> Delete forever</button>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}
        </div>
    );
};

const inp = "w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[var(--gold)]";

const Labeled = ({ label, children, className = "" }) => (
    <label className={`block ${className}`}>
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)] block mb-1">{label}</span>
        {children}
    </label>
);

const Toggle = ({ label, value, onChange, testid }) => (
    <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[var(--gold)]" data-testid={testid} />
        <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">{label}</span>
    </label>
);

const InfoTilesEditor = ({ tiles, onChange }) => {
    const setTile = (i, patch) => onChange(tiles.map((t, idx) => idx === i ? { ...t, ...patch } : t));
    const addTile = () => onChange([...tiles, { icon: "calendar", label: "", value: "" }]);
    const remove = (i) => onChange(tiles.filter((_, idx) => idx !== i));
    return (
        <div className="mt-5 p-4 rounded-2xl border border-white/10 bg-black/20" data-testid="ann-tiles-editor">
            <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)]">Info tiles (up to 4, card style only)</p>
                {tiles.length < 4 && (
                    <button onClick={addTile} className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] inline-flex items-center gap-1" data-testid="ann-tile-add">
                        <Plus size={11} /> Add
                    </button>
                )}
            </div>
            {tiles.length === 0 && <p className="text-xs text-[var(--text-muted)] italic">No tiles yet.</p>}
            <div className="space-y-2">
                {tiles.map((t, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`ann-tile-${i}`}>
                        <select value={t.icon} onChange={(e) => setTile(i, { icon: e.target.value })} className={`${inp} col-span-3`}>
                            {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                        </select>
                        <input value={t.label} onChange={(e) => setTile(i, { label: e.target.value })} placeholder="Label" className={`${inp} col-span-4`} />
                        <input value={t.value} onChange={(e) => setTile(i, { value: e.target.value })} placeholder="Value" className={`${inp} col-span-4`} />
                        <button onClick={() => remove(i)} className="col-span-1 w-8 h-8 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 flex items-center justify-center" aria-label="Remove tile">
                            <X size={12} />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};
